import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled, { createGlobalStyle } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { HealthProgramStatisticsResponse, ScreeningInsightsResponse } from '@estercare/ester-shared'
import { theme } from '../../../../theme/theme'
import { printAccentBar } from '../../../../theme/print'
import { BodySmall, BodyTiny, BodyTinyMedium } from '../../../styles/Typography'
import { formatDateForAPI, getDateFnsLocale } from '../../../../utils/dates'
import { useSelectUserOrganisationName } from '../../../../state/selectors'
import { Testimonial } from '../../../../api/ester-b2b-api/private/analytics/types'
import { SummarySection } from '../sections/SummarySection'
import { EducationalSection } from '../sections/EducationalSection'
import { HealthProgramSection } from '../sections/HealthProgramSection'
import { OrganisationNeedsSection } from '../sections/OrganisationNeedsSection'
import { TestimonialsSection } from '../sections/TestimonialsSection'
import { CompetenceSection } from '../sections/CompetenceSection'
import { getSummaryMetrics } from '../../../../utils/statistics/summary'
import workplaceLogo from '../../../../assets/esterCareWorkplaceLogo.svg'

const PRINT_ZOOM = 0.8
const PRINT_CHART_WIDTH = 760
const PRINT_LAYOUT_WIDTH = 860
const PRINT_CHART_HEIGHT = 180
const BRAND_NAME = 'EsterCare Workplace Solutions'

const loadImage = (src: string) =>
  new Promise<void>((resolve) => {
    const image = new Image()
    image.onload = () => resolve()
    image.onerror = () => resolve()
    image.src = src
  })

const nextPaint = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

// The report is laid out off screen, so fonts and the logo start loading once it has painted.
// Printing before they arrive gives a PDF with fallback fonts and the logo's alt text.
const waitForAssets = async () => {
  await nextPaint()
  await Promise.all([document.fonts.ready, loadImage(workplaceLogo)])
}

type Props = {
  insights?: ScreeningInsightsResponse
  healthProgramStatistics?: HealthProgramStatisticsResponse
  testimonials: Testimonial[]
  onDone: () => void
}

/** Mounted only while exporting: prints once it is in the DOM and calls onDone when the dialog closes. */
export const PrintReport = ({ insights, healthProgramStatistics, testimonials, onDone }: Props) => {
  const { t, i18n } = useTranslation()
  const organisationName = useSelectUserOrganisationName()
  const hasPrinted = useRef(false)
  const date = format(new Date(), 'd MMMM yyyy', { locale: getDateFnsLocale(i18n.language) })
  const fileName = `${BRAND_NAME} ${formatDateForAPI(new Date())}`

  useEffect(() => {
    if (hasPrinted.current) return
    hasPrinted.current = true

    const previousTitle = document.title
    const finish = () => {
      window.removeEventListener('afterprint', finish)
      document.title = previousTitle
      onDone()
    }

    // Chrome uses document.title as the default PDF filename
    document.title = fileName
    window.addEventListener('afterprint', finish)
    waitForAssets().then(() => {
      try {
        window.print()
      } catch {
        finish()
      }
    })
  }, [fileName, onDone])

  return (
    <>
      <PrintGlobalStyle />
      {createPortal(
        <Root aria-hidden data-print-report>
          <Cover>
            <CoverLogo src={workplaceLogo} alt={BRAND_NAME} />
            <CoverMeta>
              <CoverEyebrow>{organisationName}</CoverEyebrow>
              <CoverDate>{t('admin.statistics.reportGeneratedAt', { date })}</CoverDate>
            </CoverMeta>
          </Cover>
          <Block>
            <Accent />
            <SummarySection metrics={getSummaryMetrics(insights)} />
          </Block>
          <Block>
            <EducationalSection />
          </Block>
          <Block>
            <OrganisationNeedsSection
              insights={insights}
              chartWidth={PRINT_CHART_WIDTH}
              chartHeight={PRINT_CHART_HEIGHT}
              chartAnimated={false}
            />
          </Block>
          <Block>
            <HealthProgramSection statistics={healthProgramStatistics} />
          </Block>
          <Block>
            <TestimonialsSection testimonials={testimonials} />
          </Block>
          <Block>
            <CompetenceSection statistics={healthProgramStatistics} />
          </Block>
          <Footnote>{t('admin.statistics.reportFooter')}</Footnote>
          <PageLogo src={workplaceLogo} alt="" />
        </Root>,
        document.body
      )}
    </>
  )
}

// Only active while the report is mounted, so printing any other view is unaffected.
const PrintGlobalStyle = createGlobalStyle`
  @media print {
    @page {
      size: A4 portrait;
      margin: 16mm 14mm;
    }

    html,
    body {
      background-color: ${theme.color.white};
    }

    * {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    /* Everything else in body: the app root, modals, and tooling injected on preview deploys. */
    body > :not([data-print-report]) {
      display: none;
    }

    h1,
    h2,
    h3,
    h4 {
      break-after: avoid;
    }
  }
`

const Root = styled.div`
  position: absolute;
  top: 0;
  left: -${PRINT_LAYOUT_WIDTH * 2}px;
  width: ${PRINT_LAYOUT_WIDTH}px;

  @media print {
    position: static;
    width: auto;
    zoom: ${PRINT_ZOOM};
    background-color: ${theme.color.white};
    color: ${theme.color.black};
  }
`

const Cover = styled.header`
  break-inside: avoid;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.large}px;
  border-bottom: 1px solid ${theme.color.beigeDark};
`

const CoverLogo = styled.img`
  height: 40px;
`

const CoverMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing.tiny}px;
`

const CoverEyebrow = styled(BodyTinyMedium)`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${theme.color.plum};
`

const CoverDate = styled(BodySmall)`
  color: ${theme.color.greyBlue};
`

const Accent = styled.div`
  ${printAccentBar}
`

// Sections that render nothing must not leave a gap behind.
const Block = styled.section`
  margin-bottom: ${theme.spacing.large}px;

  &:empty {
    display: none;
  }
`

const Footnote = styled(BodyTiny)`
  color: ${theme.color.grey5};
  margin-top: ${theme.spacing.large}px;
`

const PageLogo = styled.img`
  position: fixed;
  bottom: 0;
  left: 0;
  height: 6mm;
`
